import React, { useContext } from 'react'
import { useControlledState } from '@react-stately/utils'
import { useTheme } from 'styled-components'
import { SvgRemove } from '@chilipiper/icons/src/design-system'
import { AriaProps } from '../../types'
import { Modal } from '../../../core-components/modal/Modal'
import { Box, Flex, VerticalDivider } from '../../../core-components'
import { IconButton } from '../../action'

type LabelProps =
  | {
      'aria-label': string
      'aria-labelledby'?: never
    }
  | {
      'aria-label'?: never
      'aria-labelledby': string
    }

type Props = LabelProps & {
  children: React.ReactNode
  isOpen?: boolean
  onOpenChange?: (isOpen: boolean) => void
  trigger: ((triggerProps: AriaProps) => React.ReactNode) | undefined
}

const NestingContext = React.createContext(0)
const MAX_NESTING = 2

export const InContextModal = ({ children, ...props }: Props) => {
  const nesting = Math.min(useContext(NestingContext), MAX_NESTING)
  const [isModalOpen, setIsModalOpen] = useControlledState(props.isOpen, false, props.onOpenChange)
  const topSpace = 5 + nesting * 2
  const leftSpace = 12 + nesting
  const { space } = useTheme()
  return (
    <NestingContext.Provider value={nesting + 1}>
      <Modal
        animation='slideFromRight'
        isClickOutsideDismissable={false}
        isKeyboardDismissable={false}
        isOpen={isModalOpen}
        onOpenChange={setIsModalOpen}
        mt={topSpace}
        ml={leftSpace}
        w={`calc(100% - ${space[leftSpace as keyof typeof space]})`}
        h={`calc(100% - ${space[topSpace as keyof typeof space]})`}
        {...props}
      >
        <Flex bg='bg/page' borderTopLeftRadius={12} overflow='hidden' w='100%' h='100%'>
          <Box flexGrow={1} overflow='auto' maxHeight={`calc(100vh - ${topSpace}px)`}>
            {children}
          </Box>
          <VerticalDivider />
          <IconButton
            icon={SvgRemove}
            label='Close'
            size={7}
            mt={3}
            mx={2}
            variant='moderate'
            hasTooltip={false}
            onClick={() => setIsModalOpen(false)}
          />
        </Flex>
      </Modal>
    </NestingContext.Provider>
  )
}
